import clsx from 'clsx';
import * as styles from '../styles/containers/Target.less';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import { withTranslation, WithTranslation } from 'react-i18next';
import { filterTree, expandFilteredNodes } from '../utils/filters';
import NodeTree, { TreeNodeData } from '../components/target/NodeTree';
import SelectedNode from '../containers/SelectedNode';
import Error from '../components/common/Error';
import Loader from '../components/common/Loader';
import { useActions } from '../hooks/useActions';

const Target = ({ t }: WithTranslation) => {
  const actions = useActions();
  const params = useParams();
  const targetId = params.targetId;

  const selectedNode = useSelector((store: any) => store.tree.selectedNode);
  const showDisabledTargets = useSelector((store: any) => store.tree.showDisabledTargets);
  const tree = useSelector((store: any) => store.tree.tree);
  const error = useSelector((store: any) => store.tree.error);
  const errorMessage = useSelector((store: any) => store.tree.errorMessage)
    || 'Unfortunately the service is down. Please, contact Your System Administrator.';

  const filterRef = useRef<HTMLInputElement>(null);
  const disabledToggleRef = useRef<HTMLInputElement>(null);

  // componentDidMount: load the tree for this target (re-runs if the target changes).
  useEffect(() => {
    if (targetId) {
      actions.resetTree();
      actions.fetchTree(targetId);
    }
  }, [targetId, actions]);

  const onNodeToggle = (node: TreeNodeData, toggled: boolean) => {
    if (!selectedNode._id) {
      // Only select node on 1st tree item click. Don't toggle.
      actions.selectNode(node, false);
    } else if (selectedNode._id === node._id) {
      // If selected node is clicked again, toggle it.
      actions.selectNode(node, toggled, false);
    } else {
      // Otherwise reverse clicked item toggle.
      actions.selectNode(node, !toggled);
    }
  };

  const onAddTarget = () => {
    const nodeId = selectedNode._id;
    let customerName = selectedNode.customerName;
    let customerId = selectedNode.customerId;

    // A new target inherits customer info. When the selected node has none, this
    // looks for it among the ROOT's children.
    //
    // ⚠️ TWO PRE-EXISTING BUGS, preserved verbatim — fixing them changes which
    // customer a new target gets, which is a data decision, not a typing one:
    //   1. The loop tests `children[j]` but then assigns from `children[i]`.
    //      After the first loop `i` is the SELECTED node's index, and that node
    //      is the one we already know has no customer — so the search always
    //      writes back the same empty values it was trying to replace.
    //   2. `typeof x !== undefined` compares a string against undefined and is
    //      therefore always true. It should be `!== 'undefined'`. Harmless only
    //      because the other conditions in the && chain do the real work.
    // Note too that it scans the root's children rather than actual ancestors,
    // so it only ever finds a customer for a target one level down.
    if (!customerName) {
      const treeJS = tree;
      let selectedIndex = -1;
      let i = 0;
      for (i = treeJS.children.length - 1; i >= 0; i--) {
        if (nodeId === treeJS.children[i]._id) {
          selectedIndex = i;
          break;
        }
      }
      if (selectedIndex > 0) { // search upwards from selectedIndex
        for (let j = selectedIndex - 1; j >= 0; j--) {
          if (treeJS.children[j].customerId
            && typeof treeJS.children[j].customerId !== undefined
            && treeJS.children[j].customerId > 0) {
            customerId = treeJS.children[i].customerId;
            customerName = treeJS.children[i].customerName;
            break;
          }
        }
      } else { // user path root
        customerName = treeJS.customerName;
        customerId = treeJS.customerId;
      }
    }

    const target = {
      ...selectedNode,
      name: 'New target',
      parent: selectedNode._id,
      customerName,
      customerId,
      extra: {
        ...selectedNode.extra,
        c: {
          ...selectedNode.extra?.c,
          c71: '',  // QR-code title
          c72: '',  // QR-code URL
          c73: '',  // QR-code scale
          c74: ''   // QR-code id
        }
      }
    };
    actions.addTarget(target, targetId);
  };

  const onFilterKeyUp = useCallback(() => {
    const filter = filterRef.current?.value.trim() ?? '';
    if (filter.length === 0) {
      return actions.fetchTree(targetId);
    }
    const filtered = expandFilteredNodes(filterTree(tree, filter), filter);
    return actions.filterTree(filtered);
  }, [actions, targetId, tree]);

  const debouncedFilterKeyUp = useMemo(() => debounce(onFilterKeyUp, 250), [onFilterKeyUp]);
  useEffect(() => () => debouncedFilterKeyUp.cancel(), [debouncedFilterKeyUp]);

  const onDisabledTargetToggle = () => {
    actions.showDisabledTargets(disabledToggleRef.current?.checked);
  };

  if (error) {
    return <Error message={errorMessage} />;
  }
  if (Object.keys(tree).length === 0) {
    return <Loader className="pure-u-1" style={{ padding: "2em", textAlign: "center" }} />;
  }

  return (
    <div className="pure-g">
      <div className={clsx(styles.left, 'pure-u-1-3')} >
        <form className={clsx(styles.filter, 'pure-form')} >
          <fieldset>
            <input type="search" placeholder="Filter"
              onKeyUp={debouncedFilterKeyUp}
              ref={filterRef} />
          </fieldset>
          <label>
            <input
              type="checkbox"
              onChange={onDisabledTargetToggle}
              ref={disabledToggleRef} />
            {t('target.showDisabled')}
          </label>
        </form>
        {/*
          cloneDeep because NodeTree's filterDisabledTargets MUTATES what it is
          given, in place, during render. It re-clones the whole tree on every
          render — a perf suspect on large trees (see the
          target-editor-slow-node-select notes). useMemo would help; pruning into
          a new structure inside NodeTree would remove the need entirely.
        */}
        <NodeTree
          data={cloneDeep(tree)}
          showDisabledTargets={showDisabledTargets}
          onToggle={onNodeToggle} />
        {selectedNode._id && <div className={styles.buttons}>
          <button className="button success" onClick={onAddTarget}>
            <i className="fa fa-plus-square"></i> {t('target.editor.button.add')}
          </button>
        </div>}
      </div>
      <SelectedNode
        className="pure-u-2-3"
        params={params} />
    </div>
  );
};

export default withTranslation('common')(Target);
